import { useState, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import { toast } from "react-toastify";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { useForm } from "react-hook-form";
import { iOrderDetails } from "core/kangarooTypes/iOrderDetails";
import Skeleton from "react-loading-skeleton";
import { getUsers } from "core/Services/AdminServices";
import { iUser } from "core/kangarooTypes/iUser";
import { updateOrder } from "core/Services/CommonServices";
import { useTranslation } from "react-i18next";

interface AssignDriverDialogProps {
  isOpen: boolean;
  onClose: () => void;
  order?: iOrderDetails;
  orderIds?: number[];
  onAssignmentComplete?: () => void;
}

const AssignDriverDialog = ({
  isOpen,
  onClose,
  order,
  orderIds,
  onAssignmentComplete,
}: AssignDriverDialogProps) => {
  const { t } = useTranslation();
  const [drivers, setDrivers] = useState<iUser[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      driver_id: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      fetchDrivers();
      reset();
    }
  }, [isOpen, reset]);

  const fetchDrivers = async () => {
    try {
      setIsLoading(true);
      const response = await getUsers({
        role: "driver",
        active: 1,
        getAll: 1,
      });
      setDrivers(response.data);
    } catch (error) {
      toast.error(t("assignDriverDialog.failedToLoadDrivers"));
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: { driver_id: string }) => {
    try {
      setIsSubmitting(true);

      if (order) {
        await updateOrder(
          {
            driver_id: parseInt(data.driver_id),
          },
          order.id
        );
        toast.success(t("assignDriverDialog.driverAssignedSuccess"));
      } else if (orderIds && orderIds.length > 0) {
        const orderPromises = orderIds.map((orderId) =>
          updateOrder(
            {
              driver_id: parseInt(data.driver_id),
            },
            orderId
          )
        );
        await Promise.all(orderPromises);

        toast.success(
          t("assignDriverDialog.driverAssignedMultipleSuccess", {
            count: orderIds.length,
          })
        );
      }

      onAssignmentComplete?.();
      onClose();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          t("assignDriverDialog.failedToAssignDriver")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">
            {order
              ? t("assignDriverDialog.title")
              : t("assignDriverDialog.titleMultiple", {
                  count: orderIds?.length,
                })}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          {order && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">
                {t("assignDriverDialog.orderId")}
              </p>
              <p className="font-medium">{order.id}</p>
            </div>
          )}

          {orderIds && orderIds.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">
                {t("assignDriverDialog.selectedOrders")}
              </p>
              <div className="flex flex-wrap gap-2">
                {orderIds.map((id) => (
                  <span
                    key={id}
                    className="bg-blue-100 text-blue-800 text-xs font-medium rounded px-2 py-1"
                  >
                    Order #{id}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("assignDriverDialog.selectDriver")}
            </label>
            {isLoading ? (
              <Skeleton height={40} />
            ) : (
              <FormInput
                name="driver_id"
                type="select"
                icon={<ChevronDown size={20} />}
                placeholder={t("assignDriverDialog.selectDriverPlaceholder")}
                register={register}
                errors={errors}
                selectOptions={drivers.map((driver) => ({
                  value: driver.id.toString(),
                  label: `${driver.first_name} ${driver.last_name} (${driver.phone_number})`,
                }))}
              />
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outlined"
              className="w-24"
            >
              {t("assignDriverDialog.cancel")}
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={isLoading}
              className="w-24"
            >
              {t("assignDriverDialog.assign")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignDriverDialog;
